"use client";

import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";
import { Banner } from "@/components/Banner";
import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { BindableInput } from "./BindableInput";
import { cn } from "@/lib/utils";

/**
 * V2 renderer for `display=ConfigBindableNumber`. Literal mode is a numeric
 * input; variable mode shows a chip when the stored value is exactly
 * `:var[<ref>]`. The picker filters to number-typed sources. Shares the
 * bind affordance + chip semantics with the other bindable fields via
 * {@link BindableInput} (number `dataType`).
 */
export function BindableNumberField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  disabled,
  placeholder,
  isRequired,
  showOptionalLabel,
  error,
  clearsToEmpty,
  allowNegative,
  integerOnly,
}: {
  nodeId: string;
  fieldKey: string;
  label: string;
  tooltip?: string;
  description?: string;
  disabled?: boolean;
  placeholder?: string;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  error?: string;
  clearsToEmpty?: boolean;
  allowNegative?: boolean;
  integerOnly?: boolean;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path);
  const value: string =
    typeof raw === "string" ? raw : typeof raw === "number" ? String(raw) : "";
  const resolvedLabel = useMemo(
    () => label || humanize(fieldKey),
    [label, fieldKey]
  );
  const invalid = !!error;

  return (
    <div className="flex flex-col gap-1">
      <NoxusFormLabel
        label={resolvedLabel}
        tooltip={tooltip}
        name={fieldKey}
        noMargin
        isDisabled={disabled}
        isRequired={isRequired}
        showOptionalLabel={showOptionalLabel}
      />
      <div
        className={cn(
          invalid && "rounded-md ring-1 ring-inset ring-amber-400/70"
        )}
      >
        <BindableInput
          nodeId={nodeId}
          dataType="number"
          clearsToEmpty={clearsToEmpty || !isRequired}
          allowNegative={allowNegative}
          integerOnly={integerOnly}
          isRequired={isRequired}
          inputName={fieldKey}
          value={value}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(next) =>
            form.setValue(path, next, {
              shouldDirty: true,
              shouldTouch: true,
            })
          }
        />
      </div>
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      {error ? <Banner label={error} style="warning" size="sm" /> : null}
    </div>
  );
}

function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
